import { LoanTerms } from "data/supabase";
import { tokenIdsFromRange } from "logic/utils";
import { decodeAbiParameters, encodeAbiParameters, Hash, Hex, keccak256 } from "viem";

import { LOAN_TERMS_V2_ABI, LoanTermsV2AbiStruct } from "./abis";

const IRM_OPTIONS_ABI = [
  { name: "principalAndInterestStubPayment", type: "bool" },
  { name: "gracePeriodDuration", type: "uint64" },
  { name: "gracePeriodRate", type: "uint256" },
] as const;

export function encodeInterestRateOptions(
  gracePeriodDuration: number,
  gracePeriodRate: bigint,
  principalAndInterestStubPayment = false,
): Hex {
  return encodeAbiParameters(IRM_OPTIONS_ABI, [
    principalAndInterestStubPayment,
    BigInt(gracePeriodDuration),
    gracePeriodRate,
  ]);
}

export function decodeInterestRateOptions(options: Hex) {
  const [principalAndInterestStubPayment, gracePeriodDuration, gracePeriodRate] = decodeAbiParameters(
    IRM_OPTIONS_ABI,
    options,
  );
  return { principalAndInterestStubPayment, gracePeriodDuration: Number(gracePeriodDuration), gracePeriodRate };
}

const LOAN_TERMS_V2_HASH_ABI = [{ name: "chainId", type: "uint256" }, ...LOAN_TERMS_V2_ABI] as const;

export function computeHash(chainId: number, terms: LoanTermsV2AbiStruct): Hash {
  return keccak256(encodeAbiParameters(LOAN_TERMS_V2_HASH_ABI, [BigInt(chainId), terms]));
}

export function loanToAbiTerms(loan: LoanTerms): LoanTermsV2AbiStruct {
  return {
    expiration: BigInt(loan.expiration),
    borrower: loan.borrower,
    currencyToken: loan.erc20,
    collateralToken: loan.erc721,
    repaymentSpec: {
      totalDurationDays: loan.totalDurationDays,
      day: loan.repaymentDay,
      timezoneOffsetSeconds: loan.timezoneOffsetSeconds,
    },
    interestRateSpec: {
      model: loan.interestRateModel,
      options: encodeInterestRateOptions(
        loan.gracePeriodDuration,
        loan.gracePeriodRate,
        loan.principalAndInterestStubPayment,
      ),
    },
    collateralTokenIds: tokenIdsFromRange([loan.startTokenId, loan.endTokenId]),
    trancheSpecs: loan.trancheSpecs.map((tranche) => ({
      lender: tranche.lender,
      amount: BigInt(tranche.amount),
      rate: BigInt(tranche.rate),
    })),
    feeSpecs: loan.feeSpecs.map((fee) => ({
      kind: fee.kind,
      recipient: fee.recipient,
      model: fee.model,
      options: fee.options,
    })),
    approvalAddresses: loan.approvalAddresses,
    options: loan.options,
  };
}
